import type { ReactNode } from "react";
import { ControlBarProvider } from "./Internal/ControlBarContext";
import {
  ControlBarLabelProvider,
  type ControlBarLabelPosition,
} from "./Internal/ControlBarLabelContext";
import { Menu } from "../Menu/Menu";
import { ActionBar } from "./ActionBar";

interface ControlBarProps {
  /** Labeled filter controls (ControlBarItem wrappers) — rendered first */
  filters?: ReactNode;
  /** Action buttons — rendered after filters */
  actions?: ReactNode;
  /** Menu items — auto-wrapped in the ActionBar's overflow kebab, rendered last. Pass Button children directly. */
  menu?: ReactNode;
  /**
   * Any value ("always" | "responsive") routes actions + menu + leading through
   * the overflow ActionBar: the buttons that fit show with their full label, the
   * rest fold into the kebab. Omit for plain actions that should never overflow.
   */
  actionCollapse?: "always" | "responsive" | undefined;
  /**
   * Accepted for compatibility with the page header's responsive-fold prop
   * (ENG-5878). It's a no-op here: the overflow ActionBar already folds to the
   * kebab ONLY when the row is genuinely cramped and measures it, which is
   * exactly `foldActions="whenNarrow"`. Kept so the header keeps compiling.
   */
  foldActions?: boolean | undefined;
  /** Label placement for every ControlBarItem in this bar. Defaults to "beside". */
  labelPosition?: ControlBarLabelPosition | undefined;
  /**
   * Non-collapsing content pinned to the LEFT of the action group (page-header
   * stats). It rides inside the measured region so the whole [leading | actions
   * | kebab] cluster packs to the right edge while the overflow math folds
   * actions into the kebab. Only honoured when `actionCollapse` is set.
   */
  leading?: ReactNode;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function ControlBar({
  filters,
  actions,
  menu,
  actionCollapse,
  // `foldActions` is intentionally not destructured: it's accepted on the type
  // for the page header (ENG-5878) but is a no-op — the overflow ActionBar
  // already folds only when the row is cramped.
  labelPosition = "beside",
  leading,
}: ControlBarProps) {
  if (!filters && !actions && !menu && !leading) return undefined;

  // Any actionCollapse ("always" | "responsive") routes actions + menu +
  // leading through the overflow ActionBar: the buttons that fit show with
  // their full label, the rest fold into the kebab. The consumer must give the
  // ControlBar a flex-1 min-w-0 slot so the bar has a real width to measure.
  const overflow = !!actionCollapse;

  return (
    <ControlBarLabelProvider value={labelPosition}>
      <div
        className={`flex ${overflow ? "min-w-0 flex-1" : "flex-wrap"} ${labelPosition === "above" ? "items-start" : "items-center"} gap-3`}
      >
        {filters && (
          <ControlBarProvider value="filters">{filters}</ControlBarProvider>
        )}
        {filters && (actions || menu || (overflow && leading)) && (
          <div className="h-5 w-px bg-border" />
        )}
        {overflow ? (
          (actions || menu || leading) && (
            <ControlBarProvider value="actions">
              {/* min-w-0 + flex-1 gives the ActionBar a concrete, shrinkable
                  width to measure against. */}
              <div className="flex min-w-0 flex-1">
                <ActionBar menu={menu} leading={leading}>
                  {actions}
                </ActionBar>
              </div>
            </ControlBarProvider>
          )
        ) : (
          <>
            {actions && (
              <ControlBarProvider value="actions">{actions}</ControlBarProvider>
            )}
            {menu && <Menu>{menu}</Menu>}
          </>
        )}
      </div>
    </ControlBarLabelProvider>
  );
}
